// src/components/analytics/MetricComparison.jsx
import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';

/**
 * MetricComparison component for comparing metrics with targets or benchmarks
 */
const MetricComparison = ({
  title,
  currentValue,
  targetValue,
  unit = '',
  tooltip = '',
  isPercentage = false,
  reverseProgress = false,
  formatValue = null,
  color = 'primary',
}) => {
  const theme = useTheme();

  // Calculate percentage for progress bar
  let percentage = 0;
  if (targetValue && targetValue !== 0) {
    percentage = (currentValue / targetValue) * 100;
    
    // Cap at 100% for display purposes
    if (!reverseProgress && percentage > 100) {
      percentage = 100;
    }
    
    // For reverse metrics (where lower is better)
    if (reverseProgress) {
      if (percentage > 100) {
        percentage = 100;
      }
      percentage = 100 - percentage;
    }
  }

  // Format value function
  const formatDisplayValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Use custom formatter if provided
    if (formatValue) {
      return formatValue(value);
    }
    
    // Default formatting
    if (isPercentage) {
      return `${value.toFixed(1)}%`;
    }
    
    if (typeof value === 'number') {
      // Format large numbers
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'k';
      } else if (value % 1 !== 0) {
        // Handle decimals
        return value.toFixed(1);
      }
    }
    
    return value.toString();
  };

  // Determine color based on progress
  const getProgressColor = () => {
    if (!reverseProgress) {
      // Normal case (higher is better)
      if (percentage >= 100) return theme.palette.success.main;
      if (percentage >= 70) return theme.palette[color].main;
      if (percentage >= 40) return theme.palette.warning.main;
      return theme.palette.error.main;
    } else {
      // Reverse case (lower is better)
      if (percentage >= 90) return theme.palette.success.main;
      if (percentage >= 60) return theme.palette[color].main;
      if (percentage >= 30) return theme.palette.warning.main;
      return theme.palette.error.main;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Title with tooltip */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography 
          variant="body2" 
          color="textSecondary"
          sx={{ 
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {title}
          {tooltip && (
            <Tooltip title={tooltip}>
              <InfoIcon 
                fontSize="small" 
                sx={{ 
                  ml: 0.5, 
                  color: alpha(theme.palette.text.secondary, 0.5),
                  fontSize: 14,
                }} 
              />
            </Tooltip>
          )}
        </Typography>
        
        {/* Values */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500,
              mr: 0.5,
            }}
          >
            {formatDisplayValue(currentValue)}
            {unit && !isPercentage && unit}
          </Typography>
          
          <Typography variant="caption" color="textSecondary">
            / {formatDisplayValue(targetValue)}
            {unit && !isPercentage && unit}
          </Typography>
        </Box>
      </Box>
      
      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.divider, 0.3),
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            backgroundColor: getProgressColor(),
          },
        }}
      />
    </Box>
  );
};

export default MetricComparison;